import { FormInput, FormInputColor, FormSelect, FormState, FormTextArea, Labeled } from 'component_utils/FormUtils'
import { cast, itemFilter } from 'component_utils/utils'
import ProcessingButton from 'components/ProcessingButton'
import SimpleModal from 'components/SimpleModal'
import { Tab, UncontrolledTabs } from 'components/uncontrolled_tabs'
import { ISimpleUserDetails, IUserTaskTemplate } from 'interfaces'
import React, { FC, useState } from 'react'
import { FaTimes, FaTrash } from 'react-icons/fa'
import { Badge, Button, FormGroup, Input, Label, Table } from 'reactstrap'
import { useApi } from 'utils/API'
import Translation from 'utils/Language/Translation'
import * as immutable from 'object-path-immutable';
import { fromPairs } from 'lodash'
import { useTranslations } from 'utils/language'

const TemplateEditor: FC<{
  trigger: any, 
  users: ISimpleUserDetails[],  
  reloadTasks: any,
  reloadTemplates: any,
}> = ({ 
  trigger,
  users,
  reloadTasks,
  reloadTemplates,
}) => {
  const [state, setState] = useState<any>({})
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [baseTemplate, setBaseTemplate] = useState<IUserTaskTemplate>(null)
  const api = useApi()
  const T = useTranslations()

  const toTemplate = (): IUserTaskTemplate => {
    return {
      id: baseTemplate?.id,
      createdAt: baseTemplate?.createdAt,
      updatedAt: baseTemplate?.updatedAt,

      version: baseTemplate?.version ?? 0,

      priority: state.priority || 0,
      borderColor: state.borderColor || '',
      title: state.title || '',
      text: state.text || '',

      tags: [],
      users: selectedUsers.map(it => parseInt(it)),
      parameters: fromPairs((state?.parameters || []).map((it: any) => [it.name, it.value])),
      buttons: (state?.buttons || []).map((it: any) => ({
        id: it.id,
        color: it.color,
        title: it.title,
        params: it.params.split(",").filter((it: any) => it)
      }))
    }
  }

  return (
    <SimpleModal
      title=""
      fullscreen
      trigger={open => trigger((defaultState: any, defaultUser: any, template: IUserTaskTemplate) => {
        setState(defaultState)
        setSelectedUsers(defaultUser)
        setBaseTemplate(template)
        open()
      })}
      buttons={close => [
        <ProcessingButton onClick={async() => {
          await api.userTasks.templates.save(toTemplate())
          reloadTemplates()
          close()
        }}>
          {baseTemplate !== null ? (
            <Translation name="T.misc.saveTemplate" />
          ) : (
            <Translation name="T.misc.saveAsTemplate" />
          )}
        </ProcessingButton>,
      ]}
    >
      <FormState state={state} setState={setState} disableFormWrapper>
        <UncontrolledTabs>
          <Tab name={<Translation name="T.misc.general"/>}>
            <Labeled title={<Translation name="T.misc.title"/>}>
              <FormInput path='title'/>
            </Labeled>
            <Labeled title={<Translation name="T.misc.text"/>}>
              <FormTextArea path='text' rows={5}/>
            </Labeled>
            <Labeled title={<Translation name="T.misc.priority"/>}>
              <FormSelect
                path='priority'
                options={[
                  { value: 0, label: T("T.misc.priorities.very-low") },
                  { value: 20, label: T("T.misc.priorities.low") },
                  { value: 10, label: T("T.misc.priorities.normal") },
                  { value: 30, label: T("T.misc.priorities.high") },
                  { value: 40, label: T("T.misc.priorities.very-high") },
                ]}
              />
            </Labeled>
            <Labeled title={<Translation name="T.misc.borderColor"/>}>
              <FormInputColor path='borderColor'/>
            </Labeled>

            <Table striped>
              <thead>
                <tr>
                  <th className='w-50'><Translation name="T.misc.name"/></th>
                  <th className='w-50'><Translation name="T.misc.value"/></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cast<any[]>(state?.parameters || []).map((it: any, idx) => (
                  <tr key={idx}>
                    <td>
                      <FormInput path={`parameters.${idx}.name`}/>
                    </td>
                    <td>
                      <FormInput path={`parameters.${idx}.value`}/>
                    </td>
                    <td>
                      <Button color='danger' onClick={() => {
                        setState((old: any) => immutable.del(old, 'parameters.'+idx))
                      }}>
                        <FaTrash/>
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3}>
                    <Button block color='primary' onClick={() => {
                      setState((old: any) => immutable.push(old, 'parameters', { name: '', value: '' }))
                    }}>
                      <Translation name="T.misc.add"/>
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Tab>
          <Tab name={<Translation name="T.misc.users"/>}>
            <FormGroup>
              <Label for='user-selector'>User selection</Label>
              <Input id='user-selector' type='select' onChange={e => {
                const value = e.target.value
                setSelectedUsers(old => [...old, value])
                e.target.value = ''
              }}>
                <option value=""></option>
                {users.filter(it => !selectedUsers.includes(''+it.userid)).map(user => {
                  return (<option key={user.userid} value={user.userid}>{user.username}</option>)
                })}
              </Input>
            </FormGroup>

            <div className='w-100'>
              {selectedUsers.map(userid => {
                const user = users.find(it => (''+it.userid) === userid)
                return (
                  <Badge key={userid} pill className='mr-1'>
                    {user.username}
                    <Button className='p-0' onClick={() => setSelectedUsers(old => old.filter(itemFilter([userid])))}>
                      <FaTimes/>
                    </Button>
                  </Badge>
                )
              })}
            </div>
          </Tab>
          <Tab name={<Translation name="T.misc.buttons"/>}>
            <Table striped>
              <thead>
                <tr>
                  <th className='w-25'><Translation name="T.misc.id"/></th>
                  <th className='w-25'><Translation name="T.misc.color"/></th>
                  <th className='w-25'><Translation name="T.misc.title"/></th>
                  <th className='w-25'><Translation name="T.misc.params"/></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cast<any[]>(state?.buttons || []).map((it: any, idx) => (
                  <tr key={idx}>
                    <td>
                      <FormInput path={`buttons.${idx}.id`}/>
                    </td>
                    <td>
                      <FormInput path={`buttons.${idx}.color`}/>
                    </td>
                    <td>
                      <FormInput path={`buttons.${idx}.title`}/>
                    </td>
                    <td>
                      <FormInput path={`buttons.${idx}.params`}/>
                    </td>
                    <td>
                      <Button color='danger' onClick={() => {
                        setState((old: any) => immutable.del(old, 'buttons.'+idx))
                      }}>
                        <FaTrash/>
                      </Button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={5}>
                    <Button block color='primary' onClick={() => {
                      setState((old: any) => immutable.push(old, 'buttons', { id: '', color: '', title: '', params: '' }))
                    }}>
                      <Translation name="T.misc.add"/>
                    </Button>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Tab>
        </UncontrolledTabs>
      </FormState>
    </SimpleModal>
  )
}

export default TemplateEditor